import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="floorplans--pdf-gestures"
//
// The plan's PDF preview (`Floorplans::PdfPreviewComponent`), moved and zoomed
// the way the plan itself is: drag the page to move it; pinch the trackpad —
// which the browser sends as Ctrl + wheel — or Ctrl/⌘ + wheel to zoom.
//
// The app's PDF viewer (stimulus-pdf-viewer) zooms only from its own buttons
// and with two fingers on a touch screen, and has no hand tool. This adds both
// around it, through the viewer's own `setScale`, and changes nothing in it: a
// touch screen keeps its own pinch and scroll.
const MIN_SCALE = 0.25
const MAX_SCALE = 5
// How fast one wheel step zooms: the scale is multiplied by e^(−deltaY · rate).
const WHEEL_ZOOM_RATE = 0.01
// How close a pinched scale must come to a listed zoom to show as it: within
// half a percent, where both would read as the same percentage.
const LISTED_SCALE_TOLERANCE = 0.005

export default class extends Controller {
  connect() {
    // Not a data-action: a wheel listener the page can cancel must not be
    // passive, and Stimulus registers wheel actions passive.
    this.element.addEventListener("wheel", this.#zoom, { passive: false })
  }

  disconnect() {
    this.element.removeEventListener("wheel", this.#zoom)
    this.#release()
  }

  /** data-action="pointerdown->floorplans--pdf-gestures#grab" */
  grab(event) {
    if (event.button !== 0 || event.pointerType === "touch") return
    const pages = event.target.closest(".pdf-pages-container")
    if (!pages || !this.element.contains(pages)) return

    // Not a text selection, nor the mouse events that would start one.
    event.preventDefault()
    this.#pan = {
      pages,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      left: pages.scrollLeft,
      top: pages.scrollTop,
    }
    pages.dataset.panning = "true"
    pages.setPointerCapture?.(event.pointerId)
    pages.addEventListener("pointermove", this.#move)
    pages.addEventListener("pointerup", this.#release)
    pages.addEventListener("pointercancel", this.#release)
  }

  // The drag under way, or null.
  #pan = null

  #move = (event) => {
    const pan = this.#pan
    if (!pan || event.pointerId !== pan.pointerId) return
    pan.pages.scrollLeft = pan.left - (event.clientX - pan.x)
    pan.pages.scrollTop = pan.top - (event.clientY - pan.y)
  }

  #release = () => {
    const pan = this.#pan
    if (!pan) return
    this.#pan = null
    delete pan.pages.dataset.panning
    pan.pages.releasePointerCapture?.(pan.pointerId)
    pan.pages.removeEventListener("pointermove", this.#move)
    pan.pages.removeEventListener("pointerup", this.#release)
    pan.pages.removeEventListener("pointercancel", this.#release)
  }

  #zoom = (event) => {
    if (!event.ctrlKey && !event.metaKey) return
    const viewer = this.#viewer()
    if (!viewer) return

    // Ours, not the browser's: it would zoom the whole page instead.
    event.preventDefault()
    const scale = Math.min(
      MAX_SCALE,
      Math.max(
        MIN_SCALE,
        viewer.getScale() * Math.exp(-event.deltaY * WHEEL_ZOOM_RATE),
      ),
    )
    viewer.setScale(scale)
    this.#showScale(scale)
  }

  // The toolbar's zoom box, brought in line with a zoom it did not choose, as
  // its own + / − do. Left on "Fit page", picking "Fit page" again would be no
  // change, and the browser fires none (CONEK-9147). A scale it has no option
  // for shows on a hidden one of its own, the way pdf.js does.
  #showScale(scale) {
    const select = this.element.querySelector(
      "[data-pdf-viewer-target~='zoomSelect']",
    )
    if (!select) return

    const listed = Array.from(select.options).find(
      (option) =>
        !option.disabled &&
        Math.abs(Number(option.value) - scale) < LISTED_SCALE_TOLERANCE,
    )
    if (listed) {
      select.value = listed.value
      return
    }

    let custom = select.querySelector("option[data-custom-scale]")
    if (!custom) {
      custom = document.createElement("option")
      custom.dataset.customScale = ""
      custom.hidden = true
      custom.disabled = true
      select.prepend(custom)
    }
    custom.value = String(scale)
    custom.textContent = `${Math.round(scale * 100)}%`
    custom.selected = true
  }

  /** The viewer's core — what its own zoom buttons and pinch scale — or null. */
  #viewer() {
    const element = this.element.querySelector(
      "[data-controller~='pdf-viewer']",
    )
    if (!element) return null
    return (
      this.application.getControllerForElementAndIdentifier(
        element,
        "pdf-viewer",
      )?.pdfViewer?.viewer ?? null
    )
  }
}
